import axios, { AxiosError } from "axios";
import { attachAuthInterceptors } from "@cyngn/auth-react";
import { toast } from "react-toastify";
import { ZodError } from "zod";

import { FMSLogger } from "FMSLogger";
import { sendTelemetryEvent } from "./faroUtils";
import "./authTokenStore";
import { redirectToLogin } from "./authRedirect";
import { applyObservabilityHeaders } from "./observabilityHeaders";

const networkLogger = FMSLogger.byPrefix("network-logs");

const isDevelop =
  window._env_.REACT_APP_ENV === "development" ||
  window._env_.REACT_APP_ENV === "localhost";
export const API_URL = !isDevelop
  ? `https://${window.location.host}/`
  : window._env_.REACT_APP_API_URL || "";
const REACT_APP_WSS_URL = !isDevelop
  ? `wss://${window.location.host}/`
  : window._env_.REACT_APP_WSS_URL || "";
const API_VERSION = window._env_.REACT_APP_API_VERSION || "v1";
export const REACT_APP_WSS_URL_API = `${REACT_APP_WSS_URL}api/${API_VERSION}/ws`;

export const BASE = `${API_URL}api/${API_VERSION}`;

const instance = axios.create({
  baseURL: BASE,
  withCredentials: true,
});

// The bearer and the refresh-and-retry on 401 belong to the shared library (DEV-5751): it
// sends the ID token, skips the login endpoints, and awaits the provider's single coalesced
// refresh, so parallel 401s cost one token request. Registered first so a 401 is retried
// before the logging interceptor below reports it, as the app's own pair did.
attachAuthInterceptors(instance, {
  onAuthFailure: () => {
    // A refused refresh has already cleared the session; send the user to log in again.
    redirectToLogin();
  },
});

instance.interceptors.response.use(
  (response) => {
    networkLogger.debug(
      "API response",
      response.config.url as string,
      response.data
    );
    return response;
  },
  async (error: AxiosError) => {
    networkLogger.error(
      "API error",
      error.message,
      error.code ?? "",
      error.config?.url ?? "",
      error.response?.data ?? ""
    );
    if (!axios.isCancel(error)) {
      sendTelemetryEvent(error, "API error");
    }
    throw error;
  }
);

instance.interceptors.request.use(function (config) {
  applyObservabilityHeaders(config);
  networkLogger.debug(
    "API request",
    config.url as string,
    config.method as string,
    config.data ?? ""
  );
  return config;
});

export default instance;

export const showNetworkErrorToast = (error: unknown) => {
  if (error instanceof ZodError) {
    toast.error(
      `API validation data error, ${JSON.stringify(error.issues[0])}`
    );
    networkLogger.error("API ZodError", error.issues);
    return;
  }
  const baseError = error as BaseError;
  const errorDetail = baseError?.response?.data?.detail;
  const errorMessage = `API request failed!
      URL: ${baseError?.config?.url}
      Method: ${baseError?.config?.method.toUpperCase()}
      Error code: ${baseError?.response?.status}
      ${errorDetail ? `Details: ${errorDetail}` : ""}`;
  toast.error(errorMessage);
};
